import {
	getGetAccountPaymentMethodsQueryKey,
	useGetAccountPaymentMethods,
} from '@cocoonspace/sdk-js/domains/account/account'
import { usePaymentMethodCreate } from '@cocoonspace/shared/hooks/cocoon-api'
import { zodResolver } from '@hookform/resolvers/zod'
import { CardElement, useElements } from '@stripe/react-stripe-js'
import type { StripeCardElement } from '@stripe/stripe-js'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import type { ComponentProps } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { Form } from '~/components/ui/form'
import { FormControlledInput } from '~/components/ui/form-controlled-input'
import { Input } from '~/components/ui/input'
import { StripeCardInput } from '~/domains/payments/components/atoms/stripe-card-input'
import { useTracking } from '~/providers/tracking-provider'

export interface FormValues {
	title?: string
	name: string
	card: StripeCardElement
}

export type PmFormValues = z.infer<typeof schema>

const nonemptyMessage = 'errors:forms.required'

const schema = z.object({
	title: z.string().optional(),
	name: z.string().min(1, nonemptyMessage),
	card: z.any(),
})

export const FormAddNewCard = ({
	onSubmitSuccess,
	addFormButtonVariant = 'default',
}: {
	addFormButtonVariant?: ComponentProps<typeof Button>['variant']
	onSubmitSuccess?: VoidFunction
}) => {
	const router = useRouter()
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const { track } = useTracking()
	const pmCreateMutation = usePaymentMethodCreate()
	const elements = useElements()

	const { data: paymentMethods } = useGetAccountPaymentMethods({
		query: {
			select: (res) => res.data.filter((pm) => pm.type === 'card') || [],
		},
	})

	const form = useForm<FormValues>({
		resolver: zodResolver(schema),
		defaultValues: {
			name: '',
			title: '',
			card: undefined,
		},
	})

	const onSubmit = ({ title, name, card }: FormValues) =>
		pmCreateMutation
			.mutateAsync({
				type: 'card',
				title: title || '',
				name,
				card,
			})
			.then((res) => {
				if (res?.error) {
					toast.error(res.error.error.message!)
				} else {
					toast.success(t('myAccountPaymentMethods:notifications.cardAdded'))
					queryClient.invalidateQueries({
						queryKey: getGetAccountPaymentMethodsQueryKey(),
					})
					onSubmitSuccess?.()

					form.reset()
					elements?.getElement(CardElement)?.clear()

					track.paymentMethodUpdate({ action: 'add', route: router.route })
				}
			})
			.catch(() => {
				toast.error(t('errors:generic'))
			})

	return (
		<Card>
			<CardHeader>
				<CardTitle>
					{t('myAccountPaymentMethods:add_payment_method.label')}

					<span className='ml-2 pb-[2px] font-normal text-xs'>
						{t('myAccountPaymentMethods:add_payment_method.max')}
					</span>
				</CardTitle>
			</CardHeader>

			<CardContent>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)}>
						<FormControlledInput
							name='title'
							label={t('myAccountPaymentMethods:cardTitle')}
							control={form.control}
							render={({ field }) => <Input {...field} />}
						/>

						<FormControlledInput
							name='name'
							label={t('myAccountPaymentMethods:cardOwnerName')}
							control={form.control}
							render={({ field }) => <Input {...field} />}
							required
						/>

						<FormControlledInput
							name='card'
							label={t('myAccountPaymentMethods:cardNumber')}
							control={form.control}
							render={({ field }) => (
								<StripeCardInput
									onChange={field.onChange}
									onError={(message) =>
										message
											? form.setError('card', { message })
											: form.clearErrors('card')
									}
								/>
							)}
							required
						/>

						<Button
							data-testid='add-payment-card-button'
							variant={addFormButtonVariant}
							disabled={
								!!(paymentMethods?.length && paymentMethods?.length >= 3)
							}
							type='submit'
							className='w-full'
						>
							{t('myAccountPaymentMethods:add')}
						</Button>
					</form>
				</Form>
			</CardContent>
		</Card>
	)
}
